import React, { useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import { View, Button, Grid, GridColumn, Icon, colors } from '@fairygodboss/ui';

import { verifiedBenefitsPropType } from './constants';

export const benefitTypeMap = {
    remote_work_policy: 'house_20',
    backup_child_care: 'baby_20',
    unpaid_paternity: 'stroller_20',
    paid_paternity: 'stroller_20',
    unpaid_maternity: 'stroller_20',
    paid_maternity: 'stroller_20',
    lactation: 'bottle_20',
    short_term_disability: 'wheelchair_20',
    paid_adoptive: 'support_20',
    child_birth: 'baby_20',
    fertility: 'baby_20',
    on_off_ramp_parental_leave: 'stroller_20',
    elder_care: 'pills_20',
    child_care_subsidies: 'baby_20',
    onsite_child_care: 'baby_20',
    coaching: 'whistle_20',
    sabbatical: 'calendar_20',
    diversity_performance: 'ribbon_20',
    diversity_recruiting: 'magnifyingGlass_20',
    networking: 'chat_20',
    sponsorship_program: 'money_20',
    unconscious_bias_training: 'checklist_20',
    post_maternity: 'woman_20',
    care_taking_pto: 'support_20',
    part_time_policy: 'clock_20',
    maternity_leave_coaching: 'stroller_20',
    succession_planning: 'trophy_20',
};

const BenefitListItem = ({ svgVariant, text, ...others }) => (
    <GridColumn size={{ _: 12, md: 6 }}>
        <View display="flex" pb="24px" {...others}>
            <Icon variant={benefitTypeMap[svgVariant]} fill="none" />
            <View as="p" margin="0" fontSize="16px" lineHeight="20px" pl="16px">
                {text}
            </View>
        </View>
    </GridColumn>
);

export const Benefits = ({ verifiedBenefits, companyName }) => {
    const [isCollapsable, setIsCollapsable] = useState(
        verifiedBenefits?.length > 6
    );
    const [collapsed, setCollapsed] = useState(isCollapsable);
    const [benefits, setBenefits] = useState(verifiedBenefits);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const collapsableLimit = isMobile ? 3 : 6;
        setIsCollapsable(verifiedBenefits?.length > collapsableLimit);
    }, [isMobile, verifiedBenefits]);

    const handleResize = useCallback(() => {
        const html = document?.documentElement;
        const windowWidth = window?.innerWidth || html?.clientWidth;
        const _isMobile = windowWidth && windowWidth < 600;
        if (!isMobile) {
            if (!_isMobile && verifiedBenefits?.length) {
                if (collapsed) {
                    setBenefits(verifiedBenefits.slice(0, 6));
                    setIsMobile(_isMobile);
                }
            } else {
                setIsMobile(_isMobile);
            }
        } else if (isMobile && windowWidth >= 600) {
            setIsMobile(false);
        }
    }, [isMobile, verifiedBenefits, collapsed]);

    useEffect(() => {
        handleResize();
    }, [handleResize]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    useEffect(() => {
        const benefitsLength = benefits?.length;
        const benefitLengthLimit = isMobile ? 3 : 6;
        if (benefitsLength > benefitLengthLimit && collapsed) {
            const _benefits = benefits.slice(0, benefitLengthLimit);
            setBenefits(_benefits);
            return;
        }

        if (!collapsed && benefitLengthLimit !== verifiedBenefits?.length) {
            setBenefits(verifiedBenefits);
        }
    }, [collapsed, benefits, verifiedBenefits, isMobile]);

    if (!verifiedBenefits?.length) {
        return null;
    }
    return (
        <View
            borderBottomStyle="solid"
            borderWidth="1px"
            borderColor={colors.secondary[20]}
            pt="large"
            pb={isCollapsable ? '22px' : 'medium'}
        >
            <Grid>
                <GridColumn size={{ md: 10.5 }}>
                    <View
                        as="h3"
                        fontFamily="Open Sans, sans-serif"
                        fontSize="16px"
                        fontWeight={700}
                        lineHeight="20px"
                        margin="0 0 24px 0"
                        color={colors.neutral[100]}
                    >
                        What are {companyName} perks and benefits
                    </View>
                </GridColumn>
            </Grid>
            <Grid>
                {benefits.map((b) => (
                    <BenefitListItem
                        key={`benefit-${b.benefit_type}`}
                        svgVariant={b.benefit_type}
                        text={b.text}
                    />
                ))}
            </Grid>
            {isCollapsable ? (
                <Grid>
                    <GridColumn size={{ md: 10.5 }}>
                        <View display="flex" justifyContent="center">
                            <Button
                                variant="primaryText"
                                size="medium"
                                onPress={() => setCollapsed(!collapsed)}
                                rightIcon={
                                    collapsed ? (
                                        <Icon
                                            variant="chevron_down_16"
                                            outlineColor={colors.primary[60]}
                                        />
                                    ) : (
                                        <Icon
                                            variant="chevron_up_16"
                                            outlineColor={colors.primary[60]}
                                        />
                                    )
                                }
                            >
                                Show {collapsed ? 'more' : 'less'}
                            </Button>
                        </View>
                    </GridColumn>
                </Grid>
            ) : undefined}
        </View>
    );
};

Benefits.propTypes = {
    verifiedBenefits: verifiedBenefitsPropType,
    companyName: PropTypes.string,
};

Benefits.defaultProps = {
    verifiedBenefits: [],
    companyName: null,
};
